import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight } from "lucide-react";

export const SEO_PAGES = {
  "mutuelle-famille": {
    type: "mutuelle_france",
    eyebrow: "Mutuelle santé famille",
    title: "Mutuelle santé famille : protégez tous vos proches",
    intro: "Une complémentaire santé adaptée aux familles couvre les soins courants, l'orthodontie des enfants, l'optique et l'hospitalisation, avec souvent la gratuité à partir du 3ᵉ enfant selon les contrats.",
    points: ["Gratuité possible dès le 3ᵉ enfant (selon contrat)", "Prise en charge de l'orthodontie et de l'optique enfant", "Soins courants et hospitalisation pour toute la famille", "Tiers payant étendu"],
    faqs: [
      { q: "Peut-on ajouter un enfant en cours d'année ?", a: "Oui, la plupart des contrats permettent d'ajouter un ayant droit à la naissance ou lors d'un changement de situation." },
      { q: "L'orthodontie est-elle remboursée ?", a: "Les niveaux de garanties confort et maximal prévoient généralement des forfaits orthodontie. Un conseiller vérifie les plafonds réels." },
    ],
  },
  "mutuelle-senior": {
    type: "mutuelle_france",
    eyebrow: "Mutuelle santé senior",
    title: "Mutuelle santé senior : des garanties renforcées",
    intro: "Les besoins évoluent avec l'âge : hospitalisation, dentaire, optique, audioprothèses et dépassements d'honoraires. Une mutuelle senior renforce ces postes sans questionnaire médical dans les parcours standards.",
    points: ["Renforcement hospitalisation et dépassements d'honoraires", "Prise en charge audioprothèses et dentaire", "Aucune donnée médicale détaillée demandée", "Délais de carence à vérifier contrat par contrat"],
    faqs: [
      { q: "Y a-t-il un questionnaire médical ?", a: "Dans nos parcours standards, aucune donnée médicale détaillée n'est demandée. L'éligibilité dépend des conditions documentées de chaque contrat." },
      { q: "Les audioprothèses sont-elles couvertes ?", a: "Oui, le dispositif 100% Santé et les forfaits complémentaires sont pris en compte selon les garanties choisies." },
    ],
  },
  "mutuelle-independants": {
    type: "mutuelle_france",
    eyebrow: "Mutuelle indépendant / TNS",
    title: "Mutuelle santé pour indépendants et TNS",
    intro: "En tant que travailleur non salarié, vous pouvez bénéficier d'une complémentaire santé éligible au dispositif Madelin, déductible fiscalement sous conditions.",
    points: ["Contrats éligibles à la loi Madelin (sous conditions)", "Garanties modulables selon votre activité", "Couverture hospitalisation et soins courants", "Accompagnement sur les justificatifs"],
    faqs: [
      { q: "Qu'est-ce que la loi Madelin ?", a: "Elle permet, sous conditions, de déduire les cotisations de complémentaire santé de votre revenu imposable. Un conseiller vérifie votre éligibilité." },
    ],
  },
  "assurance-expatrie": {
    type: "expatrie",
    eyebrow: "Assurance santé expatrié",
    title: "Assurance santé expatrié : partez l'esprit tranquille",
    intro: "Que vous soyez salarié détaché, en contrat local, indépendant ou retraité à l'étranger, une assurance santé internationale couvre vos frais médicaux, l'hospitalisation et l'assistance rapatriement.",
    points: ["Couverture au premier euro ou en complément de la CFE", "Hospitalisation et soins courants dans le monde entier", "Assistance et évacuation médicale selon les contrats", "Maternité selon disponibilité"],
    faqs: [
      { q: "Premier euro ou complément CFE ?", a: "L'assurance au premier euro rembourse dès le premier euro de dépense. En complément CFE, elle intervient après la CFE. Le choix dépend de votre budget et de votre situation." },
    ],
  },
  "assurance-internationale": {
    type: "expatrie",
    eyebrow: "Assurance santé internationale",
    title: "Assurance santé internationale pour expatriés et familles",
    intro: "Une couverture internationale vous suit dans plusieurs pays, idéale pour les carrières mobiles et les familles vivant à l'étranger.",
    points: ["Couverture multi-pays", "Réseau de soins international", "Prise en charge directe dans de nombreux établissements", "Garanties modulables"],
    faqs: [
      { q: "Suis-je couvert lors de voyages ?", a: "La plupart des contrats internationaux incluent une couverture lors de déplacements, avec parfois des limites pour le pays d'origine. À vérifier par contrat." },
    ],
  },
  "assurance-premier-euro": {
    type: "expatrie",
    eyebrow: "Assurance au premier euro",
    title: "Assurance santé au premier euro",
    intro: "L'assurance au premier euro prend en charge vos dépenses de santé dès le premier euro, sans intervention préalable de la Sécurité sociale française ou de la CFE.",
    points: ["Remboursement dès le premier euro", "Idéale sans affiliation CFE", "Garanties complètes possibles", "Adaptée aux expatriations longues"],
    faqs: [
      { q: "Faut-il être affilié à la CFE ?", a: "Non, l'assurance au premier euro fonctionne indépendamment de la CFE. Elle peut être un choix autonome de couverture." },
    ],
  },
  "assurance-cfe": {
    type: "expatrie",
    eyebrow: "Complémentaire CFE",
    title: "Assurance complémentaire CFE",
    intro: "La Caisse des Français de l'Étranger (CFE) maintient vos droits à la Sécurité sociale. Une complémentaire privée prend le relais sur les restes à charge.",
    points: ["Complète les remboursements de la CFE", "Réduit vos restes à charge", "Maintien des droits en France", "Combinaison CFE + privé optimisée par un conseiller"],
    faqs: [
      { q: "La CFE suffit-elle seule ?", a: "La CFE rembourse sur la base des tarifs français, souvent insuffisants à l'étranger. Une complémentaire couvre la différence." },
    ],
  },
};

export default function SeoPage({ slug }) {
  const page = SEO_PAGES[slug];
  if (!page) return null;
  const simLink = page.type === "mutuelle_france" ? "/simulateur/mutuelle-france" : "/simulateur/assurance-expat";
  const simLabel = page.type === "mutuelle_france" ? "Comparer les mutuelles santé" : "Comparer les assurances expatrié";

  return (
    <PublicLayout>
      <section className="bg-gradient-to-b from-blue-50/60 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 bg-blue-50 px-3 py-1 rounded-full">{page.eyebrow}</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-5 leading-tight">{page.title}</h1>
          <p className="text-lg text-slate-600 mt-5 leading-relaxed">{page.intro}</p>
          <Link to={simLink}><Button className="mt-8" size="lg">{simLabel} <ArrowRight size={18} className="ml-2" /></Button></Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid sm:grid-cols-2 gap-4">
          {page.points.map((p) => (
            <Card key={p} className="p-5 flex items-start gap-3">
              <CheckCircle2 className="text-indigo-600 mt-0.5 shrink-0" size={20} />
              <span className="text-slate-700">{p}</span>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">Questions fréquentes</h2>
        <Accordion type="single" collapsible>
          {page.faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="bg-white rounded-xl px-5 mb-3 border">
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <Card className="mt-8 p-6 text-center bg-slate-50">
          <p className="text-slate-700">Prêt à comparer ? Lancez votre simulation en 2 minutes.</p>
          <Link to={simLink}><Button className="mt-4">{simLabel}</Button></Link>
        </Card>
      </section>
    </PublicLayout>
  );
}
